import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Combobox } from './ui/combobox';
import { Plus, Trash2, ClipboardPaste, ChevronDown, ChevronUp } from 'lucide-react';

const productSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  code: z.string().min(1, 'Product code is required'),
  brand: z.string().min(1, 'Brand is required'),
  category: z.string().min(1, 'Category is required'),
  quantity: z.coerce.number().min(0, 'Quantity must be positive'),
  price: z.coerce.number().min(0.001, 'Price must be greater than 0'),
  cost: z.coerce.number().min(0, 'Cost must be positive'),
  discount_rate: z.coerce.number().min(0).max(100).default(0),
  description: z.string().optional(),
  specifications: z.array(
    z.object({ label: z.string().min(1, 'Label required'), value: z.string() })
  ).default([]),
  is_active: z.boolean().default(true),
});

export function ProductForm({ initialData, categories = [], onSubmit, onCancel, onDeleteImage }) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productSchema),
    defaultValues: initialData || {
      name: '',
      code: '',
      brand: '',
      category: '',
      quantity: 0,
      price: 0,
      cost: 0,
      discount_rate: 0,
      description: '',
      specifications: [],
      is_active: true,
    },
  });

  // Dynamic spec rows
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'specifications' });

  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const handleBulkParse = () => {
    if (!bulkText.trim()) return;
    
    const lines = bulkText.split('\n');
    const newSpecs = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      // Try to split by Tab, then multi-space, then colon
      let parts = trimmedLine.split('\t');
      if (parts.length < 2) parts = trimmedLine.split(/\s{2,}/);
      if (parts.length < 2) {
        // Find first colon
        const colonIndex = trimmedLine.indexOf(':');
        if (colonIndex !== -1) {
          parts = [trimmedLine.substring(0, colonIndex), trimmedLine.substring(colonIndex + 1)];
        }
      }
      
      if (parts.length >= 2) {
        const label = parts[0].trim();
        const value = parts.slice(1).join(' ').trim();
        if (label && value) {
          newSpecs.push({ label, value });
        }
      }
    });

    if (newSpecs.length > 0) {
      replace(newSpecs);
      setBulkText('');
      setShowBulkPaste(false);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset({
        ...initialData,
        specifications: Array.isArray(initialData.specifications) ? initialData.specifications : [],
      });
    } else {
      reset({
        name: '',
        code: '',
        brand: '',
        category: '',
        quantity: 0,
        price: 0,
        cost: 0,
        discount_rate: 0,
        description: '',
        specifications: [],
        is_active: true,
      });
    }
  }, [initialData, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="product-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Product Name *</label>
          <Input {...register('name')} placeholder="e.g. Printer X" />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Product Code *</label>
          <Input {...register('code')} placeholder="e.g. PRT-001" />
          {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Brand *</label>
          <Input {...register('brand')} placeholder="e.g. HP" />
          {errors.brand && <p className="text-red-500 text-xs mt-1">{errors.brand.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Category *</label>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <Combobox
                options={categories.map(c => ({ label: c, value: c }))}
                value={field.value}
                onChange={(val) => field.onChange(val.toUpperCase())}
                placeholder="Select or type category..."
                allowArbitraryInput={true}
              />
            )}
          />
          {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Quantity</label>
          <Input type="number" {...register('quantity')} />
          {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Price (KWD) *</label>
          <Input type="number" step="0.001" {...register('price')} />
          {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Cost (KWD)</label>
          <Input type="number" step="0.001" {...register('cost')} />
          {errors.cost && <p className="text-red-500 text-xs mt-1">{errors.cost.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Discount (%)</label>
          <Input type="number" step="0.01" {...register('discount_rate')} placeholder="0" />
          {errors.discount_rate && <p className="text-red-500 text-xs mt-1">{errors.discount_rate.message}</p>}
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          {...register('description')}
          rows={3}
          placeholder="General product overview or marketing text..."
          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-[#8acb95]"
        />
      </div>

      {/* Specifications */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium">Specifications</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowBulkPaste(!showBulkPaste)}
              className={`flex items-center gap-1 text-xs font-medium border px-2 py-1 rounded transition-colors ${
                showBulkPaste 
                ? 'bg-slate-100 border-slate-300 text-slate-700' 
                : 'text-slate-500 hover:text-slate-700 border-slate-200'
              }`}
            >
              <ClipboardPaste className="w-3.5 h-3.5" />
              {showBulkPaste ? 'Cancel Paste' : 'Bulk Paste'}
              {showBulkPaste ? <ChevronUp className="w-3 h-3 ml-0.5" /> : <ChevronDown className="w-3 h-3 ml-0.5" />}
            </button>
            <button
              type="button"
              onClick={() => append({ label: '', value: '' })}
              className="flex items-center gap-1 text-xs text-[#8acb95] hover:text-[#6db87a] font-medium border border-[#8acb95]/20 px-2 py-1 rounded"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Row
            </button>
          </div>
        </div>

        {showBulkPaste && (
          <div className="mb-4 space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Paste data from website (Label [Tab/Colon] Value)</p>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Example:&#10;Model No	Belkin BoostCharge&#10;Color	White"
              rows={5}
              className="w-full text-xs font-mono p-2 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#8acb95]"
            />
            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                size="sm" 
                variant="outline" 
                onClick={() => setBulkText('')}
                className="h-7 text-xs"
              >
                Clear
              </Button>
              <Button 
                type="button" 
                size="sm" 
                onClick={handleBulkParse}
                className="bg-[#8acb95] hover:bg-[#7bb885] h-7 text-xs text-white"
              >
                Parse & Fill
              </Button>
            </div>
          </div>
        )}

        {fields.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-2">
            No specifications yet. Click "Add Row" to add specs like Processor, RAM, Storage…
          </p>
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            {/* Header */}
            <div
              className="grid grid-cols-[1fr_1fr_auto] px-3 py-2 text-xs font-bold uppercase tracking-widest text-white"
              style={{ background: 'linear-gradient(90deg, #8acb95 0%, #6db87a 100%)' }}
            >
              <span>Specification</span>
              <span>Value</span>
              <span />
            </div>
            {/* Rows */}
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2 border-t border-slate-100 items-center"
                style={{ background: index % 2 === 0 ? '#f8fafc' : '#ffffff' }}
              >
                <Input
                  {...register(`specifications.${index}.label`)}
                  placeholder="e.g. Processor"
                  className="text-sm h-8"
                />
                <Input
                  {...register(`specifications.${index}.value`)}
                  placeholder="e.g. Intel Core i5"
                  className="text-sm h-8"
                />
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-slate-400 hover:text-red-500 transition-colors p-1"
                  title="Remove row"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Images */}
      {initialData?.images && initialData.images.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">Current Images</label>
          <div className="flex flex-wrap gap-2">
            {initialData.images.filter(Boolean).map((img, idx) => (
              <div key={idx} className="relative w-16 h-16 border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden group">
                <img src={img.startsWith('http') ? img : process.env.REACT_APP_BACKEND_URL + '/files/' + img} alt="Product" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => onDeleteImage && onDeleteImage(initialData.id, idx)}
                  className="absolute top-1 right-1 bg-red-500/90 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove Image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <input type="checkbox" {...register('is_active')} className="rounded" />
        <label className="text-sm">Active</label>
      </div>
      <div className="flex gap-2 pt-2">
        <Button type="submit" className="bg-[#8acb95] text-white hover:bg-[#7bb885]">
          {initialData ? 'Update' : 'Create'} Product
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
